/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FiChevronLeft, FiChevronRight, FiImage, FiX } from 'react-icons/fi';
import PackageImage from '../common/PackageImage';

const TILES = 5;

/** Full-screen viewer: arrows, keyboard and a thumbnail strip. */
export function PackageLightbox({ images, startIndex, title, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const count = images.length;
  const go = (step) => setIndex((i) => (i + step + count) % count);

  useEffect(() => setIndex(startIndex), [startIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const current = images[index];
  if (!current) return null;

  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="pkg-modal pkg-lightbox"
    >
      <Modal.Body>
        <button
          type="button"
          className="pkg-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX />
        </button>
        <div className="pkg-lightbox__head">
          <strong>{title}</strong>
          <span>
            {index + 1} / {count}
          </span>
        </div>
        <div className="pkg-lightbox__stage">
          <PackageImage
            src={current.url}
            alt={current.caption || title}
            loading="eager"
          />
          {count > 1 && (
            <>
              <button
                type="button"
                className="pkg-lightbox__nav pkg-lightbox__nav--prev"
                onClick={() => go(-1)}
                aria-label="Previous photo"
              >
                <FiChevronLeft />
              </button>
              <button
                type="button"
                className="pkg-lightbox__nav pkg-lightbox__nav--next"
                onClick={() => go(1)}
                aria-label="Next photo"
              >
                <FiChevronRight />
              </button>
            </>
          )}
        </div>
        {current.caption && (
          <p className="pkg-lightbox__caption">{current.caption}</p>
        )}
        {count > 1 && (
          <div className="pkg-lightbox__thumbs">
            {images.map((img, i) => (
              <button
                type="button"
                key={img.url}
                className={i === index ? 'is-active' : ''}
                onClick={() => setIndex(i)}
                aria-label={`Photo ${i + 1}`}
              >
                <PackageImage src={img.url} />
              </button>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

PackageLightbox.propTypes = {
  images: PropTypes.array.isRequired,
  startIndex: PropTypes.number,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

PackageLightbox.defaultProps = {
  startIndex: 0,
  title: '',
};

/** Cover + up to four thumbnails; any tile opens the viewer there. */
function PackageGallery({ images, title }) {
  const [openAt, setOpenAt] = useState(null);
  if (!images.length) return null;
  const tiles = images.slice(0, TILES);
  const extra = images.length - tiles.length;

  return (
    <>
      <div className={`pkg-gallery pkg-gallery--${tiles.length}`}>
        {tiles.map((img, i) => (
          <button
            type="button"
            key={img.url}
            className={`pkg-gallery__tile ${
              i === 0 ? 'pkg-gallery__tile--main' : ''
            }`}
            onClick={() => setOpenAt(i)}
            aria-label={`Open photo ${i + 1}`}
          >
            <PackageImage
              src={img.url}
              alt={i === 0 ? title : ''}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            {i === tiles.length - 1 && extra > 0 && (
              <span className="pkg-gallery__more">+{extra} photos</span>
            )}
          </button>
        ))}
        <button
          type="button"
          className="pkg-gallery__all"
          onClick={() => setOpenAt(0)}
        >
          <FiImage /> View all {images.length} photos
        </button>
      </div>
      {openAt !== null && (
        <PackageLightbox
          images={images}
          startIndex={openAt}
          title={title}
          onClose={() => setOpenAt(null)}
        />
      )}
    </>
  );
}

PackageGallery.propTypes = {
  images: PropTypes.array.isRequired,
  title: PropTypes.string,
};

PackageGallery.defaultProps = {
  title: '',
};

export default PackageGallery;
